import { ReactNode } from "react";
import { FormattedMessage, defineMessages } from "react-intl";
import { ErrorMessage } from "../FormGroup";
import MarkdownEditor from "../MarkdownEditor";

import {
  BaseProjectVersionApprovalButton,
  FragmentKey,
  Messages,
} from "./Base";
import { Controller } from "react-hook-form";
import SuggestionPlugin from "../MarkdownEditor/plugins/Suggestion";

const messages = defineMessages<keyof Messages>({
  dialogTriggerCreate: { defaultMessage: "Reject" },
  dialogTriggerUpdate: { defaultMessage: "Rejected" },
  dialogTitle: { defaultMessage: `Reject submission "{version}" of {entity}` },
  createSubmit: { defaultMessage: "Reject" },
  onCreateSuccess: {
    defaultMessage: "{entity}'s submission has been rejected!",
  },
  onCreateFailure: {
    defaultMessage:
      "{entity}'s submission cannot be rejected at the moment, please retry later.",
  },
  updateSubmit: { defaultMessage: "Update rejection" },
  onUpdateSuccess: {
    defaultMessage: "{entity}'s submission rejection has been updated!",
  },
  onUpdateFailure: {
    defaultMessage:
      "{entity}'s submission rejection cannot be updated at the moment, please retry later.",
  },
  deleteSubmit: { defaultMessage: "Withdraw rejection" },
  onDeleteSuccess: {
    defaultMessage:
      "You have just withdrawn your rejection on {entity}'s submission",
  },
  onDeleteFailure: {
    defaultMessage:
      "{entity}'s submission rejection cannot be withdrawn at the moment, please retry later.",
  },
});

interface FormData {
  reason: string;
}

const errorMessages: { [K in keyof FormData]: Record<string, ReactNode> } = {
  reason: {},
};

interface Props {
  projectVersion: FragmentKey;
}

export default function ProjectVersionRejectButton(props: Props) {
  return (
    <BaseProjectVersionApprovalButton<FormData>
      fragment={props.projectVersion}
      messages={messages}
      isHidden={({ approval }) => !!approval && approval.accept}
      getDefaultValues={({ approval }) => ({
        reason: approval?.notes ?? undefined,
      })}
      getCreateInput={(data) => ({
        reject: {
          reason: data.reason,
        },
      })}
      getUpdateInput={(data) => ({
        reject: {
          reason: data.reason,
        },
      })}
    >
      {({ formState, ...form }) => {
        return (
          <>
            <p className="gap-2 inline-flex flex-row items-center">
              <FormattedMessage defaultMessage="Notes" />
            </p>
            <ErrorMessage
              error={
                typeof formState.errors.reason?.type === "string" &&
                errorMessages.reason[formState.errors.reason.type]
              }
            />

            <Controller
              control={form.control}
              name="reason"
              rules={{ maxLength: 2000 }}
              render={({ field: { onChange, onBlur, value, name, ref } }) => (
                <>
                  <MarkdownEditor
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    aria-invalid={formState.errors.reason ? "true" : "false"}
                  >
                    <SuggestionPlugin />
                  </MarkdownEditor>
                  {/*
                    Workaround to keep the browser’s native form-validation working.
                    Lexical renders content through its own pipeline, so it currently
                    bypasses the built-in validation mechanism
                  */}
                  <input
                    type="text"
                    className="sr-only"
                    aria-hidden="true"
                    tabIndex={-1}
                    name={name}
                    value={value ?? ""}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                  />
                </>
              )}
            />
          </>
        );
      }}
    </BaseProjectVersionApprovalButton>
  );
}
